import { useTranslation } from '@mukil/i18n';
import {
  Button,
  DatePicker,
  GlassCard,
  RadioGroup,
  RadioGroupItem,
} from '@jumpstone/ui-react';
import { useDatePickerLabels } from '../../../lib/use_date_picker_labels';
import type { MigrationScope } from './employee_migration_logic';

/**
 * Step 1 — Start: choose what to bring across and the cut-over date.
 * JSX-only; the validity gate (`canLeaveStart`) lives in the logic module.
 */
export function MigrationStartStep({
  scope,
  onScopeChange,
  cutoverDate,
  onCutoverChange,
  maxDate,
  canContinue,
  onNext,
}: {
  scope: MigrationScope;
  onScopeChange: (scope: MigrationScope) => void;
  cutoverDate: string;
  onCutoverChange: (value: string) => void;
  /** Latest pickable cut-over date (ISO) — the gate rejects future dates. */
  maxDate: string;
  canContinue: boolean;
  onNext: () => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const datePickerLabels = useDatePickerLabels();
  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-5">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('migration.start.description')}
        </p>

        <div>
          <h3 className="mb-2 text-sm font-semibold">
            {t('migration.scope.title')}
          </h3>
          <RadioGroup
            value={scope}
            onValueChange={(next) => {
              if (next === 'full' || next === 'payroll_only') {
                onScopeChange(next);
              }
            }}
            aria-label={t('migration.scope.title')}
            className="gap-3"
          >
            <label className="flex items-start gap-3 rounded-lg border border-[color:var(--line)] p-3">
              <RadioGroupItem value="full" />
              <span>
                <span className="block text-sm font-medium">
                  {t('migration.scope.full.label')}
                </span>
                <span className="block text-xs text-[color:var(--ink-muted)]">
                  {t('migration.scope.full.help')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-[color:var(--line)] p-3">
              <RadioGroupItem value="payroll_only" />
              <span>
                <span className="block text-sm font-medium">
                  {t('migration.scope.payroll_only.label')}
                </span>
                <span className="block text-xs text-[color:var(--ink-muted)]">
                  {t('migration.scope.payroll_only.help')}
                </span>
              </span>
            </label>
          </RadioGroup>
        </div>

        <div>
          <label
            htmlFor="migration-cutover"
            className="mb-1 block text-sm font-semibold"
          >
            {t('migration.cutover.label')}
          </label>
          <DatePicker
            id="migration-cutover"
            value={cutoverDate || null}
            max={maxDate}
            locale={locale}
            labels={datePickerLabels}
            aria-label={t('migration.cutover.label')}
            onChange={(iso) => {
              onCutoverChange(iso);
            }}
          />
          <p className="mt-1 text-xs text-[color:var(--ink-muted)]">
            {t('migration.cutover.help')}
          </p>
        </div>

        <div className="flex justify-end">
          <Button disabled={!canContinue} onClick={onNext}>
            {t('migration.button.next')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
}
